import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { IconButton, useShellLayout } from '@/ui';
import s from './Templates.module.css';

/** Master–detail only where there is room for both (IA §4: ultrawide). */
export function useMasterDetail(): boolean {
  return useShellLayout() === 'ultrawide';
}

/**
 * List template (IA §5.2): header · URL-bound filter bar · data table, with an optional
 * detail pane beside the table on ultrawide screens.
 */
export function ListTemplate({
  header,
  filters,
  children,
  detail,
  detailLabel,
  onCloseDetail,
}: {
  header: ReactNode;
  filters?: ReactNode;
  children: ReactNode;
  /** Detail for the selected row (rendered only in master–detail mode). */
  detail?: ReactNode;
  detailLabel?: string;
  onCloseDetail?: () => void;
}) {
  const masterDetail = useMasterDetail();
  const showDetail = masterDetail && !!detail;
  return (
    <div className={cx(s.list, showDetail && s.withDetail)}>
      <div className={s.listHeader}>{header}</div>
      {filters && <div className={s.listFilters}>{filters}</div>}
      <div className={s.listBody}>
        <div className={s.listTable}>{children}</div>
        {showDetail && (
          <aside className={s.detail} aria-label={detailLabel ?? t('Details')}>
            {onCloseDetail && <IconButton icon={X} label={t('Close details')} size="sm" className={s.detailClose} onClick={onCloseDetail} />}
            {detail}
          </aside>
        )}
      </div>
    </div>
  );
}
